import { useEffect, useRef } from 'react';
import { showStaleWarning, dismissStaleWarning } from '@/lib/toasts';
export type { StaleWarningAction } from '@/lib/toasts';
import type { StaleWarningAction } from '@/lib/toasts';

// One week in milliseconds. Items opened past this age get the stale warning.
// Exported so the demo fixtures can assert against the real threshold rather
// than a copy of the number (see src/mocks/fixtures/fixtures.test.ts) — a copy
// would keep passing if this were narrowed, while the demo started toasting.
export const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Fires a "this is stale" warning toast when an opened item was last worked on
 * more than a week ago (ENG-976). Stale items predate newer pipeline features,
 * so we nudge the user that some of them may not work here.
 *
 * The timestamp must be the item's LAST-ACTIVITY clock, not its creation date,
 * so that taking the suggested action actually clears the warning on the next
 * open: the lineage tip's `created_at` for MeshGen (a re-upload adds a new tip),
 * `diagnosis_at` for Diagnosis (a re-run bumps it), and `updated_at` for SimOps
 * studies (a run/edit bumps it). Keying off an immutable `created_at` made the
 * warning un-clearable and nagged actively-used items.
 *
 * Pass an optional `action` to add a call-to-action button (e.g. "Generate new
 * mesh") that points the user at fresh output with the latest pipeline. Pass
 * `undefined` as the timestamp to suppress the warning entirely (e.g. while a
 * study is actively running, or in an embedded flow that shouldn't nag).
 *
 * Re-fires when a different item is opened (the timestamp changes); a background
 * refetch of the same item does not re-fire. Reopening an item remounts the
 * route, so it fires again on each open.
 *
 * @param lastActiveAt ISO timestamp of the item's most recent activity.
 * @param message      the full, already-translated warning sentence to show.
 * @param action       optional button shown in the toast.
 */
export function useStaleWarning(
  lastActiveAt: string | null | undefined,
  message: string,
  action?: StaleWarningAction,
) {
  // Hold the latest action in a ref so a fresh inline object on each render
  // doesn't re-run the firing effect (which would dismiss + re-show the toast,
  // causing a flicker). The effect reads actionRef.current only when it fires.
  const actionRef = useRef(action);
  useEffect(() => {
    actionRef.current = action;
  });

  useEffect(() => {
    if (lastActiveAt) {
      const ageMs = Date.now() - new Date(lastActiveAt).getTime();
      if (!Number.isNaN(ageMs) && ageMs >= ONE_WEEK_MS) {
        showStaleWarning(message, actionRef.current);
      }
    }
    // Clear the warning when the user leaves this view (route unmount) or
    // switches to a different item, so it never lingers on another page.
    return () => dismissStaleWarning();
  }, [lastActiveAt, message]);
}
